// Web Bluetooth GATT — Health Thermometer service
// https://www.bluetooth.com/specifications/specs/health-thermometer-service-1-0/
const HT_SERVICE = "health_thermometer"; // 0x1809
const TEMP_MEASUREMENT_CHAR = "temperature_measurement"; // 0x2A1C

function ieee11073FloatToCelsius(bytes) {
  // bytes: little-endian IEEE-11073 32-bit FLOAT (4 bytes)
  // [mantissa: 24-bit signed, exponent: 8-bit signed]
  const mantissa = bytes[0] | (bytes[1] << 8) | (bytes[2] << 16);
  const exponent = bytes[3] > 127 ? bytes[3] - 256 : bytes[3];
  const m = mantissa > 0x7FFFFF ? mantissa - 0x1000000 : mantissa;
  return m * Math.pow(10, exponent);
}

function parseTemperatureMeasurement(dv) {
  // Byte 0 = flags. Bit 0: units (0=C, 1=F). Bit 1: timestamp present.
  const flags = dv.getUint8(0);
  const isFahrenheit = (flags & 0x01) === 1;
  const b1 = dv.getUint8(1), b2 = dv.getUint8(2), b3 = dv.getUint8(3), b4 = dv.getUint8(4);
  let value = ieee11073FloatToCelsius([b1, b2, b3, b4]);
  if (isFahrenheit) value = (value - 32) * 5 / 9;
  return value;
}

export function isBluetoothAvailable() {
  return typeof navigator !== "undefined" && !!navigator.bluetooth;
}

/**
 * Request a Bluetooth thermometer, connect, read a temperature measurement,
 * and resolve with the value in Celsius (rounded to 1 decimal).
 */
export async function readTemperatureFromBluetooth() {
  if (!isBluetoothAvailable()) {
    throw new Error("Web Bluetooth isn't supported on this device or browser.");
  }
  const device = await navigator.bluetooth.requestDevice({
    filters: [{ services: [HT_SERVICE] }],
    optionalServices: [HT_SERVICE],
  });
  const server = await device.gatt.connect();
  const service = await server.getPrimaryService(HT_SERVICE);
  const char = await service.getCharacteristic(TEMP_MEASUREMENT_CHAR);

  return new Promise((resolve, reject) => {
    let timeout = setTimeout(() => {
      try { char.stopNotifications(); } catch {}
      try { device.gatt.disconnect(); } catch {}
      reject(new Error("Timed out waiting for a reading. Take a measurement on the thermometer."));
    }, 30000);

    const onChange = (e) => {
      try {
        const value = parseTemperatureMeasurement(e.target.value);
        clearTimeout(timeout);
        char.removeEventListener("characteristicvaluechanged", onChange);
        try { char.stopNotifications(); } catch {}
        try { device.gatt.disconnect(); } catch {}
        resolve(Math.round(value * 10) / 10);
      } catch (err) {
        clearTimeout(timeout);
        reject(err);
      }
    };
    char.addEventListener("characteristicvaluechanged", onChange);
    char.startNotifications().catch(reject);
  });
}
